import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  SimpleGrid,
  RadioGroup,
  Radio,
  Stack,
  Textarea,
  useToast,
  useColorModeValue,
  Box,
  Flex,
  Text,
  InputGroup,
  InputLeftElement,
  Icon,
  Portal,
} from "@chakra-ui/react";
import {
  MdPerson,
  MdEmail,
  MdGroup,
  MdRocketLaunch,
} from "react-icons/md";
import { ReactComponent as LystoLogo } from "assets/img/lysto-logo-round.svg";
import useSubmitWaitlist from "../hooks/useSubmitWaitlist";

/**
 * A reusable waitlist signup modal component
 */
const WaitlistModal = ({ isOpen, onClose }) => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    userCount: '',
    useCRM: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastData, setToastData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const toast = useToast();
  
  // Move all color mode values to the top level, before any conditionals
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColorLight = useColorModeValue("gray.100", "gray.700");
  const bgHeaderColor = useColorModeValue("blue.50", "blue.900");
  
  // Initialize the useSubmitWaitlist hook
  const { mutateAsync: submitWaitlist } = useSubmitWaitlist();
  
  // Effect to add/remove modal-open class to body
  useEffect(() => {
    const headerElement = document.getElementById('lysto-fixed-header');
    const bodyElement = document.body;
    const originalOverflow = bodyElement.style.overflow;
    
    if (isOpen) {
      if (headerElement) {
        headerElement.style.display = 'none';
      }
      // Prevent scrolling on the body when modal is open
      bodyElement.style.overflow = 'hidden';
    } else {
      if (headerElement) {
        headerElement.style.display = 'block';
      }
      // Restore body scrolling
      bodyElement.style.overflow = originalOverflow;
    }
    
    return () => {
      if (headerElement) {
        headerElement.style.display = 'block';
      }
      // Ensure body scrolling is restored
      bodyElement.style.overflow = originalOverflow;
    };
  }, [isOpen]);
  
  // Effect to show toast after modal is closed
  useEffect(() => {
    if (!isOpen && toastData) {
      // Show the toast after modal is closed
      toast({
        ...toastData,
        position: "top-right",
        containerStyle: {
          marginTop: "60px", // Add top margin to clear the header
          zIndex: 9999999 // Higher than both modal and header
        }
      });
      // Reset toast data
      setToastData(null);
    }
  }, [isOpen, toastData, toast]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle radio button changes
  const handleRadioChange = (value) => {
    setFormData(prev => ({
      ...prev,
      useCRM: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(''); // Clear any previous error
    
    try {
      // Submit the form using the new hook
      await submitWaitlist(formData);

      // Prepare toast data but don't show it yet
      setToastData({
        title: "Waitlist Signup Successful",
        description: "Thank you for joining our waitlist! We'll be in touch soon.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      // Reset form and close modal
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        userCount: '',
        useCRM: '',
        notes: ''
      });
      onClose();
    } catch (error) {
      // Extract error message from backend response
      const message = error.response?.data?.message || 
                     error.response?.data?.error || 
                     error.message || 
                     "There was an error submitting your information. Please try again.";
      
      // Set error message to display in the modal
      setErrorMessage(message);
      
      console.error("Form submission error:", error);
      // Don't close the modal on error
    } finally {
      setIsSubmitting(false);
    }
  };

  // Always return the Portal and modal structure, but conditionally show/hide with CSS
  return (
    <>
      <Portal>
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0" 
          bottom="0"
          zIndex="999999"
          bg="rgba(0, 0, 0, 0.5)"
          display={isOpen ? "flex" : "none"} 
          alignItems="center"
          justifyContent="center"
          onClick={onClose}
          css={{
            backdropFilter: "blur(4px)",
            animation: "fadeIn 0.2s ease-in-out"
          }}
        >
          <Box
            maxWidth="680px"
            width="90%"
            bg={bgColor}
            borderRadius="xl"
            boxShadow="2xl"
            onClick={(e) => e.stopPropagation()}
            overflow="hidden"
            position="relative"
            css={{
              animation: "slideUp 0.3s ease-out"
            }}
          >
            {/* Header */}
            <Flex
              p={4}
              borderBottomWidth="1px" 
              borderColor={borderColorLight}
              bg={bgHeaderColor}
              align="center"
              justify="space-between"
            >
              <Flex align="center">
                <Box 
                  p={2} 
                  borderRadius="md"
                  overflow="hidden"
                >
                  <LystoLogo width="30px" height="30px"/>
                </Box>
                <Text fontWeight="bold" fontSize="xl">Sign up for the Lysto Waitlist</Text>
              </Flex>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </Button>
            </Flex>
            
            {/* Body */}
            <Box p={6}>
              {/* Error message display */}
              {errorMessage && (
                <Box 
                  mb={4} 
                  p={3} 
                  bg="red.50" 
                  color="red.600" 
                  borderRadius="md"
                  borderLeftWidth="4px"
                  borderLeftColor="red.500"
                >
                  <Text fontWeight="medium">Error: {errorMessage}</Text>
                </Box>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Name Fields (First and Last on same row) */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">First Name</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon as={MdPerson} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="First name"
                        focusBorderColor="blue.400"
                      />
                    </InputGroup>
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Last Name</FormLabel>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Last name"
                      focusBorderColor="blue.400"
                    />
                  </FormControl>
                </SimpleGrid>
                
                {/* Email Field */}
                <FormControl isRequired mb={4}>
                  <FormLabel fontSize="sm">Company Email</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={MdEmail} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      focusBorderColor="blue.400"
                    />
                  </InputGroup>
                </FormControl>
                
                {/* Region and User Count (same row) */}
                <Box mb={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Number of Users</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon as={MdGroup} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        name="userCount"
                        value={formData.userCount}
                        onChange={handleInputChange}
                        placeholder="Estimated number of users"
                        focusBorderColor="blue.400"
                        type="number"
                      />
                    </InputGroup>
                  </FormControl>
                </Box>
                
                {/* CRM Usage */}
                <FormControl isRequired mb={4}>
                  <FormLabel fontSize="sm">Are you currently using a CRM?</FormLabel>
                  <RadioGroup
                    name="useCRM"
                    value={formData.useCRM}
                    onChange={handleRadioChange}
                  >
                    <Stack direction="row" spacing={5}>
                      <Radio value="yes" colorScheme="blue">Yes</Radio>
                      <Radio value="no" colorScheme="blue">No</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>
                
                {/* Notes Field */}
                <FormControl mb={4}>
                  <FormLabel fontSize="sm">Additional Notes</FormLabel>
                  <Textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Tell us about your current setup or specific requirements"
                    focusBorderColor="blue.400"
                    rows={2}
                  />
                  <FormHelperText color="gray.500" fontSize="xs">
                  </FormHelperText>
                </FormControl>
              </form>
            </Box>
            
            {/* Footer */}
            <Flex
              p={4}
              borderTopWidth="1px"
              borderColor={borderColorLight}
              justify="flex-end"
            >
              <Button 
                variant="outline" 
                colorScheme="blue" 
                mr={3} 
                onClick={onClose}
                isDisabled={isSubmitting}
              >
                Close
              </Button>
              <Button 
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Submitting"
                leftIcon={<MdRocketLaunch />}
              >
                Submit
              </Button>
            </Flex>
          </Box>
        </Box>
      </Portal>
    </>
  );
};

export default WaitlistModal; 